/*
 * Cphere's reporting calendar (eastern_reporting_calendar_v1), as both
 * portals read it. Byte-identical in Cortex and Quantum
 * (src/utils/reportingCalendar.js); a parity test in each repo pins it.
 *
 * The backend decides the reporting day: Eastern midnight to Eastern midnight.
 * A portal never sends its viewer's zone and never lets the device clock
 * choose a reported day — two viewers on different coasts read the same
 * window and the same totals. A portal states the calendar the RESPONSE says
 * it used, so a label can never claim Eastern days over data read on another
 * calendar.
 *
 * Calendar dates (flight start/end, reporting days) are YYYY-MM-DD strings
 * and are never parsed as midnight instants. Instants are formatted in the
 * reporting zone only for a person to read.
 */

export const REPORTING_TIME_ZONE = "America/New_York";
export const REPORTING_TIME_ZONE_LABEL = "Eastern Time (ET)";
export const REPORTING_TIME_ZONE_ABBREVIATION = "ET";
export const REPORTING_CALENDAR_VERSION = "eastern_reporting_calendar_v1";

/*
 * The calendar the backend used before eastern_reporting_calendar_v1. A
 * response that states it is still read (and labelled as Pacific) while the
 * backend upgrade rolls out; nothing new is ever resolved in it.
 */
export const LEGACY_REPORTING_TIME_ZONE = "America/Los_Angeles";

export const ACCEPTED_REPORTING_TIME_ZONES = Object.freeze([
  REPORTING_TIME_ZONE,
  LEGACY_REPORTING_TIME_ZONE,
]);

const TIME_ZONE_LABELS = Object.freeze({
  [REPORTING_TIME_ZONE]: REPORTING_TIME_ZONE_LABEL,
  [LEGACY_REPORTING_TIME_ZONE]: "Pacific Time (PT)",
  "America/Chicago": "Central Time (CT)",
  "America/Denver": "Mountain Time (MT)",
  UTC: "Coordinated Universal Time (UTC)",
  "Etc/UTC": "Coordinated Universal Time (UTC)",
});

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isAcceptedReportingTimeZone(timeZone) {
  return ACCEPTED_REPORTING_TIME_ZONES.includes(String(timeZone || ""));
}

export function isCalendarDate(value) {
  if (typeof value !== "string") return false;
  const match = value.match(DATE_ONLY);
  if (!match) return false;
  const probe = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return probe.toISOString().slice(0, 10) === value;
}

/*
 * The calendar day an instant falls on in a zone (the reporting zone by
 * default). A bare YYYY-MM-DD is a calendar date, not an instant: it returns
 * null rather than being read as UTC midnight (which is the previous day in
 * Eastern Time).
 */
export function reportingDateOf(instant = Date.now(), timeZone = REPORTING_TIME_ZONE) {
  if (typeof instant === "string" && DATE_ONLY.test(instant.trim())) return null;
  const date = instant instanceof Date ? instant : new Date(instant);
  if (!Number.isFinite(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return null;
  }
}

export function reportingToday(now = Date.now(), timeZone = REPORTING_TIME_ZONE) {
  return reportingDateOf(now, timeZone);
}

/* Calendar arithmetic on a YYYY-MM-DD string; no zone, no DST, no device clock. */
export function addCalendarDays(dateOnly, days) {
  if (!isCalendarDate(dateOnly)) return null;
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + Number(days || 0))).toISOString().slice(0, 10);
}

/*
 * The instant (epoch ms) a calendar day begins in a zone — Eastern midnight
 * by default — found from the zone's own rules, so the spring-forward day is
 * 23 hours long and the fall-back day 25. Null for a value that is not a date.
 */
export function reportingDayStartMs(dateOnly, timeZone = REPORTING_TIME_ZONE) {
  if (!isCalendarDate(dateOnly)) return null;
  const [year, month, day] = dateOnly.split("-").map(Number);
  const wallClockAsUtc = Date.UTC(year, month - 1, day, 0, 0, 0);
  let formatter;
  try {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return null;
  }
  let instant = wallClockAsUtc;
  for (let pass = 0; pass < 3; pass += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
    const zonedAsUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second));
    const next = wallClockAsUtc - (zonedAsUtc - instant);
    if (next === instant) break;
    instant = next;
  }
  return instant;
}

/* Whole calendar days from `from` to `to` (both YYYY-MM-DD); null if either is not a date. */
export function calendarDaysBetween(from, to) {
  if (!isCalendarDate(from) || !isCalendarDate(to)) return null;
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
}

/*
 * Days from the reporting calendar's today (Eastern Time) to a calendar date,
 * for countdowns ("N days until flight start"). Display only.
 * - "YYYY-MM-DD": that date.
 * - A Date at device-local midnight is how the pages parse "YYYY-MM-DD", so
 *   it is read by its local components (that date).
 * - Any other Date is an instant: its Eastern date, the same on every device.
 */
export function reportingDaysUntil(target, now = Date.now()) {
  let key = null;
  if (target instanceof Date) {
    if (!Number.isFinite(target.getTime())) return null;
    const localMidnight = target.getHours() === 0 && target.getMinutes() === 0 &&
      target.getSeconds() === 0 && target.getMilliseconds() === 0;
    if (localMidnight) {
      const month = String(target.getMonth() + 1).padStart(2, "0");
      const day = String(target.getDate()).padStart(2, "0");
      key = `${target.getFullYear()}-${month}-${day}`;
    } else {
      key = reportingDateOf(target);
    }
  } else if (typeof target === "string" && isCalendarDate(target.trim())) {
    key = target.trim();
  }
  return calendarDaysBetween(reportingToday(now), key);
}

export function reportingTimeZoneLabel(timeZone = REPORTING_TIME_ZONE) {
  const zone = String(timeZone || "").trim();
  if (!zone) return null;
  return TIME_ZONE_LABELS[zone] || zone;
}

export function reportingTimeZoneAbbreviation(timeZone = REPORTING_TIME_ZONE) {
  const label = reportingTimeZoneLabel(timeZone);
  if (!label) return null;
  const match = label.match(/\(([A-Z]{2,4})\)$/);
  return match ? match[1] : label;
}

/*
 * The zone a reporting payload says its days are in: the canonical
 * `reportingCalendar` statement first, then the window's own `timeZone`.
 * Null when the payload states none — a surface then states nothing rather
 * than guess.
 */
export function responseReportingTimeZone(payload) {
  if (!payload || typeof payload !== "object") return null;
  const canonical = payload.canonicalReporting || payload.canonicalSummary || payload;
  return (
    canonical?.reportingCalendar?.timeZone ||
    canonical?.timeframe?.timeZone ||
    payload?.meta?.timeZone ||
    payload?.timeZone ||
    null
  );
}

/* "Reporting time zone: Eastern Time (ET)" — for the response's own calendar. */
export function reportingTimeZoneStatement(timeZone) {
  const label = reportingTimeZoneLabel(timeZone);
  return label ? `Reporting time zone: ${label}` : null;
}

const SOURCE_PLATFORM_NAMES = Object.freeze({
  facebook: "Facebook", instagram: "Instagram", meta: "Meta", tiktok: "TikTok", youtube: "YouTube",
  linkedin: "LinkedIn", x: "X", reddit: "Reddit", snapchat: "Snapchat", pinterest: "Pinterest",
  google_ads: "Google Ads", google_search: "Google Search", stackadapt: "StackAdapt", ctv: "CTV",
  amazon: "Amazon", amazon_dsp: "Amazon DSP", yahoo_dsp: "Yahoo DSP", metricool: "Metricool",
  community: "Community", ga4: "GA4", google_analytics: "Google Analytics", audio: "Audio", display: "Display",
  dooh: "DOOH", video: "Video", truth_social: "Truth Social", truthsocial: "Truth Social", rumble: "Rumble",
});
const sourcePlatformName = (key) => SOURCE_PLATFORM_NAMES[String(key || "").toLowerCase()] ||
  String(key || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const PLATFORM_FAMILIES = Object.freeze({
  meta: Object.freeze(["facebook", "instagram", "audience_network", "messenger", "meta"]),
});
// Portal keys whose canonical (backend) key is spelled differently.
const CANONICAL_PLATFORM_KEYS = Object.freeze({ truthsocial: "truth_social", ga4: "google_analytics" });
const DAY_BASIS_RANK = Object.freeze({ eastern_native: 0, source_calendar_days: 1, source_calendar_unverified: 2 });

/*
 * A response's source-calendar statement (`reportingCalendar.normalization`)
 * narrowed to the platform(s) a surface or total actually shows — a canonical
 * answer describes every platform the advertiser has. Meta covers its family.
 * Null when the statement says nothing about those platforms.
 */
export function sourceCalendarsFor(normalization, platformKeys) {
  if (!normalization || typeof normalization.status !== "string") return null;
  const requested = (Array.isArray(platformKeys) ? platformKeys : [platformKeys])
    .map((key) => String(key || "").trim().toLowerCase())
    .filter(Boolean);
  const keys = new Set(
    requested.flatMap((key) => [...(PLATFORM_FAMILIES[key] || [key]), CANONICAL_PLATFORM_KEYS[key]]).filter(Boolean)
  );
  // A Facebook or Instagram total is also described by a statement about Meta.
  if (requested.some((key) => PLATFORM_FAMILIES.meta.includes(key))) keys.add("meta");
  // A platform entry without a basis is classified from its zones; anything
  // but exactly Eastern stays unverified, never normalized.
  const withBasis = (entry = {}) => {
    if (DAY_BASIS_RANK[entry.dayBasis] !== undefined) return entry;
    const zones = Array.isArray(entry.sourceTimeZones) ? entry.sourceTimeZones : [];
    return {
      ...entry,
      dayBasis: zones.length === 1 && zones[0] === REPORTING_TIME_ZONE ? "eastern_native" : zones.length ? "source_calendar_days" : "source_calendar_unverified",
    };
  };
  const platforms = Object.fromEntries(
    Object.entries(normalization.platforms || {})
      .filter(([key]) => keys.has(String(key).toLowerCase()))
      .map(([key, entry]) => [key, withBasis(entry)])
  );
  const names = Object.keys(platforms).sort();
  if (!names.length) return null;
  const pick = (basis) => names.filter((key) => platforms[key]?.dayBasis === basis);
  return {
    status: names.every((key) => platforms[key]?.dayBasis === "eastern_native") ? "eastern_normalized" : "mixed_source_calendars",
    platforms,
    easternNativePlatforms: pick("eastern_native"),
    sourceCalendarPlatforms: pick("source_calendar_days"),
    unverifiedPlatforms: pick("source_calendar_unverified"),
  };
}

/* Two statements about the same platform: union of zones, least-verified basis. */
export function mergeSourceCalendarPlatform(a, b) {
  if (!a) return b;
  if (!b) return a;
  const rank = (entry) => DAY_BASIS_RANK[entry?.dayBasis] ?? DAY_BASIS_RANK.source_calendar_unverified;
  return {
    ...a,
    sourceTimeZones: [...new Set([...(a.sourceTimeZones || []), ...(b.sourceTimeZones || [])])].sort(),
    dayBasis: rank(a) >= rank(b) ? a.dayBasis : b.dayBasis,
  };
}

/*
 * When a response's platforms report on their own calendars, the line a
 * surface shows beside the time-zone statement — the provider SOURCE
 * calendars, kept apart from the response's reporting calendar: which
 * platforms' days are their account's days (and which zone), and which
 * platforms' zone was not captured. Null when every platform's days are
 * already Eastern days (or the response says nothing).
 */
export function sourceCalendarQualification(payload, { platforms: onlyPlatforms } = {}) {
  const canonical = payload?.canonicalReporting || payload?.canonicalSummary || payload;
  const stated = canonical?.reportingCalendar?.normalization;
  let normalization = stated;
  if (onlyPlatforms && stated && typeof stated.status === "string") {
    // A page about one platform qualifies that platform only.
    normalization = sourceCalendarsFor(stated, onlyPlatforms);
    if (!normalization) {
      const names = (Array.isArray(onlyPlatforms) ? onlyPlatforms : [onlyPlatforms]).map(sourcePlatformName).filter(Boolean);
      return names.length ? `Source calendar not stated — ${names.join(", ")}.` : null;
    }
  }
  if (!normalization) return null;
  // A total whose answers did state source calendars, but none for the
  // platforms it shows, names them rather than saying nothing.
  if (normalization.status === "not_stated" && normalization.statementsPresent) {
    const missing = (normalization.sourcesNotStatedPlatforms || [])
      .map((platform) => (typeof platform === "string" ? sourcePlatformName(platform) : platform?.label || sourcePlatformName(platform?.key)))
      .filter(Boolean);
    return missing.length ? `Source calendars not stated — ${missing.join(", ")}.` : null;
  }
  if (normalization.status !== "mixed_source_calendars") return null;
  const platforms = normalization.platforms || {};
  const listed = (keys) => (Array.isArray(keys) ? keys : []).filter((key) => typeof key === "string" && key);
  const ownCalendars = listed(normalization.sourceCalendarPlatforms).map((key) => {
    const zones = (platforms[key]?.sourceTimeZones || []).map((zone) => reportingTimeZoneLabel(zone)).filter(Boolean);
    return zones.length ? `${sourcePlatformName(key)}: ${zones.join(", ")}` : sourcePlatformName(key);
  });
  const notCaptured = listed(normalization.unverifiedPlatforms).map(sourcePlatformName);
  // An aggregate also lists answers that said nothing about their sources.
  const notStated = (Array.isArray(normalization.sourcesNotStatedPlatforms) ? normalization.sourcesNotStatedPlatforms : [])
    .map((platform) => (typeof platform === "string" ? sourcePlatformName(platform) : platform?.label || sourcePlatformName(platform?.key)))
    .filter(Boolean);
  const parts = [];
  if (ownCalendars.length) {
    parts.push(`Daily figures on account calendars, not converted to Eastern — ${ownCalendars.join("; ")}.`);
  }
  if (notCaptured.length) {
    parts.push(`Zone not captured — ${notCaptured.join(", ")}: days as each source reported them.`);
  }
  if (notStated.length) {
    parts.push(`Source calendars not stated — ${notStated.join(", ")}.`);
  }
  return parts.length
    ? parts.join(" ")
    : "Some platforms report on their own account time zone; their daily figures are shown on those days.";
}

/*
 * The qualification an aggregate total carries when some answers in it did
 * not state their calendar (`selectAggregateReportingWindow`'s
 * `reportingCalendar`): their figures are included for the same dates, but
 * their calendar is unknown — never presented as verified Eastern or Pacific.
 */
export function aggregateCalendarQualification(reportingCalendar) {
  const unstated = Array.isArray(reportingCalendar?.unstatedPlatforms) ? reportingCalendar.unstatedPlatforms : [];
  const names = unstated.map((platform) => platform?.label || platform?.key).filter(Boolean);
  if (!names.length) return null;
  const stated = reportingTimeZoneLabel(reportingCalendar?.responseTimeZone);
  return stated
    ? `Calendar not stated by ${names.join(", ")}: included for the same dates, not verified as ${stated} days.`
    : `Calendar not stated by ${names.join(", ")}: days as each source reported them, not verified as Eastern or Pacific.`;
}

/* An instant for a person to read, in the reporting zone, zone stated. */
export function formatReportingTimestamp(value, { timeZone = REPORTING_TIME_ZONE } = {}) {
  const date = value instanceof Date ? value : new Date(value);
  if (value == null || value === "" || !Number.isFinite(date.getTime())) return "";
  try {
    const text = new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
    return `${text} ${reportingTimeZoneAbbreviation(timeZone)}`;
  } catch {
    return date.toISOString();
  }
}
